<template>
  <v-container class="pa-0">
    <div v-if="graphs.length === 0">
      <template>
        <v-alert
          :value="true"
          color="info"
          icon="info"
          outline
        >
          {{ $t('availability_graph_no_spots_selected') }}
        </v-alert>
      </template>
    </div>
    <div v-else ref="chart">
      <template v-for="(graph, index) in graphs">
        <v-layout :key="graph.id">
          <v-flex xs12>
            <v-card elevation="0" class="pa-2 mb-2">
              <v-layout row
                        wrap
                        pb-2>
                <v-flex xs12>
                  <span v-html="$t('private_availabilities_label_spot_with_spot_label', { spotLabel: graph.label })"></span>
                </v-flex>
              </v-layout>
              <v-layout row
                        wrap>
                <v-flex xs12>
                  <chart :datasets="graph.datasets"
                        :bar-options="graph.barOptions"
                        :height="graph.chartHeight"
                        @generated="setLegend"></chart>
                </v-flex>
              </v-layout>
            </v-card>
            <v-divider v-if="index < (graphs.length-1)"></v-divider>
          </v-flex>
        </v-layout>
      </template>
    </div>
    <v-container  v-if="graphs.length > 0" class="legend-con" fluid grid-list-sm>
      <v-layout row wrap v-html="legend" justify-center class="ul">

      </v-layout>
    </v-container>
    <update-availabilities-modal :is-sensored="true"
                                 :parking-id="parkingId"
                                 :parking-name="parking.name"
                                 :is-availability-dialog-visible="isAvailabilityDialogVisible"
                                 @on-close-modal="closeModal"></update-availabilities-modal>
  </v-container>
</template>


<script>
import _ from 'lodash';
import store from '@/store';
import { chartsHelpers } from '@/components/commons/charts/chartsHelpers';
import Chart from '@/components/commons/charts/Chart.vue';
import UpdateAvailabilitiesModal from '@/components/commons/UpdateAvailabilitiesModal.vue';
import keys from '@/store/keys';
import { EventBus } from '@/services/eventBus';

const onCloseModalEvent = 'on-close-modal';
const onRefreshGraphsEvent = 'on-refresh-graphs';

export default {
  name: 'parking-availability-sensor-graph',
  props: ['graphData', 'dates', 'isAvailabilityDialogVisible', 'parkingId', 'generalView'],
  components: {
    [Chart.name]: Chart,
    [UpdateAvailabilitiesModal.name]: UpdateAvailabilitiesModal
  },
  watch: {
    graphData: function(value) {
      this.updateGraph();
    },
    generalView: function(value) {
      this.updateGraph();
    }
  },
  data() {
    return {
      graphs: [],
      plotUnit: 'day',
      parking: {},
      spots: [],
      legend: ''
    };
  },
  created() {
    this.parking = store.state.parkingSelected;
    this.spots = store.state.parkingSpots;
    EventBus.$on(keys.eventOnScreenSizeChanged, this.refreshGraph);
  },
  destroyed() {
    EventBus.$off(keys.eventOnScreenSizeChanged, this.refreshGraph);
  },
  methods: {
    setLegend (html) {
      this.legend = html;
    },
    async refreshGraph(params) {
      if (typeof this.$refs.chart === 'undefined') {
        return;
      }
      this.$refs.chart.getElementsByTagName('canvas')[0].style.width = 0;
      await this.updateGraph();
    },
    updateGraph() {
      if (typeof this.graphData.chartData === 'undefined') {
        return;
      }
      this.spots = store.state.parkingSpots;
      this.graphs = [];
      this.plotUnit = chartsHelpers.getPlotUnit(this.dates[0], this.dates[1]);

      let items = {};

      _.each(this.graphData.chartData, (val, index) => {
        let key = val.spotId;

        if (typeof items[key] === 'undefined') {
          items[key] = {
            id: 0,
            label: '',
            data: []
          };
        }

        let spot = _.find(this.spots, it => {
          return it.id === key;
        });

        if (typeof spot !== 'undefined') {
          items[key].label = spot.label;
          items[key].status = spot.status;
        }

        items[key].id = key;
        items[key].data.push(val);
      });

      let spotsSorted = _.sortBy(items, it => {
        return it.label;
      });

      this.$nextTick(() => {
        let displaySize = chartsHelpers.getDisplaySize(this);
        let defaultChartHeight;
        _.each(spotsSorted, (obj, index) => {
          let showLegend = false;

          if (displaySize === 'xs') {
            defaultChartHeight = 150;
          } else {
            defaultChartHeight = 45;
          }

          let barOptions = chartsHelpers.getAvailabilityDetailsParkingSensoredBarOptions(this, showLegend);

          let fillChartDataResults = chartsHelpers.fillChartData(this.graphData.chartSettings, obj.data, this.dates[0], this.dates[1], true, this.generalView);

          let date = fillChartDataResults.date;
          let dataSetsArray = fillChartDataResults.dataSetsArray;

          let datasets = {
            labels: date,
            datasets: dataSetsArray
          };

          let spotStatusI18n = this.getSpotStausI18n(obj.status);

          this.graphs.push({
            datasets: datasets,
            barOptions: barOptions,
            label: `${obj.label} (${spotStatusI18n})`,
            id: obj.id,
            chartHeight: defaultChartHeight
          });
        });
      });
    },
    closeModal() {
      this.$emit(onCloseModalEvent);
    },
    updateGraphs() {
      this.$emit(onRefreshGraphsEvent);
    },
    getSpotStausI18n(status) {
      switch (status) {
        case 'AVAILABLE':
        case 'FREE':
          return this.$i18n.t('private_parking_activity_spots_spots_status_1');
        case 'RESERVED':
          return this.$i18n.t('private_parking_activity_spots_spots_status_2');
        case 'IN_USE':
          return this.$i18n.t('private_parking_activity_spots_spots_status_3');
        case 'WRONG':
          return this.$i18n.t('private_parking_activity_spots_spots_status_4');
        case 'NO_AVAILABLE_FREE':
          return this.$i18n.t('private_parking_activity_spots_spots_status_5');
        case 'NO_AVAILABLE_OCCUPIED':
          return this.$i18n.t('private_parking_activity_spots_spots_status_6');
        case 'BROKEN':
          return this.$i18n.t('private_parking_activity_spots_spots_status_7');
        case 'OCCUPIED':
          return this.$i18n.t('private_parking_activity_spots_spots_status_8');
        default:
          return '-';
      }
    }
  }
};
</script>

<style>
  .legend-con {
    justify-content: center;
    width: inherit;
  }
  .legend-con .ul {
    list-style: none;
    padding: 0px;
    margin-top: 10px;
  }

  .legend-con .li {
    margin-bottom: 4px;
    margin-right: 10px;
  }
  .legend-con .li span {
    display: inline-block;
    font-size: 12px;
    color: gray;
    vertical-align: bottom;
  }

  .legend-con .li span.chart-legend {
      width: 20px;
      height: 20px;
      margin-right: 5px;
      border-radius: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/components/commons/ParkingAvailabilitySensorGraph.vue